import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useAgency } from './AgencyProvider';
import { connectChat, type ChatIntake } from '../services/connectChat';
import { franchiseApi, type FranchiseCarrier } from '../services/franchiseApi';
import { jiraTicketsApi, type TicketAttachment } from '../services/jiraTicketsApi';
import { LOB_KEYWORDS } from '../utils/carrierTags';
import { captureError } from '../services/posthog';
import { useChat } from './chat/useChat';
import ChatTranscript from './chat/ChatTranscript';
import ChatComposer from './chat/ChatComposer';
import { downloadTranscript } from './chat/downloadTranscript';
import { requestNotificationPermission } from './chat/browserNotification';
import type { ChatType } from './chat/types';
import HelpIcon from '@mui/icons-material/Help';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import 'amazon-connect-chatjs';
import './ChatWidget.css';

const PRE_CHAT_EMPTY = {
  insuredName: '',
  lob: '',
  lookupCode: '',
  helpDescription: '',
  contactNumber: '',
  carrierName: '',
  carrierUsername: '',
};

// The pre-chat form holds every field for every hotline, and switching hotlines doesn't clear the
// ones the previous form asked. So send the subset this hotline actually asked about — otherwise a
// Carrier Relations chat can arrive carrying an insured name typed on the way to the Agent Hotline.
function intakeFor(chatType: ChatType, answers: typeof PRE_CHAT_EMPTY): ChatIntake {
  const { helpDescription, insuredName, lob, lookupCode, contactNumber, carrierName, carrierUsername } = answers;
  if (chatType === 'carrier') return { helpDescription, contactNumber, carrierName, carrierUsername };
  if (chatType === 'agent' || chatType === 'commercial') {
    return { helpDescription, insuredName, lob, lookupCode };
  }
  return { helpDescription };
}

interface ChatWidgetProps {
  // Where the launch buttons go: a slot in the app's top bar, which isn't mounted until after the
  // first render.
  actionsContainer: HTMLElement | null;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ actionsContainer }) => {
  const { user } = useAuth();
  const { agency } = useAgency();
  const chat = useChat();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // ?chat=hotline opens the hotline picker: the public support page links here, and a chat can only
  // start from a signed-in session. The parameter is dropped so a reload or Back doesn't reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('chat') !== 'hotline') return;
    setShowConfirm(true);
    params.delete('chat');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [deptCategory, setDeptCategory] = useState('');
  const [deptSubCategory, setDeptSubCategory] = useState('');
  const [deptPhone, setDeptPhone] = useState('');
  const [deptPolicyNumber, setDeptPolicyNumber] = useState('');
  const [deptAicEmail, setDeptAicEmail] = useState('');
  const [deptDescription, setDeptDescription] = useState('');
  const [deptFiles, setDeptFiles] = useState<File[]>([]);
  const [deptSubmitting, setDeptSubmitting] = useState(false);
  const [deptSubmitted, setDeptSubmitted] = useState(false);
  const [deptTicketKey, setDeptTicketKey] = useState('');
  const [deptError, setDeptError] = useState('');
  const [deptCarrier, setDeptCarrier] = useState('');
  const [deptCarrierSearch, setDeptCarrierSearch] = useState('');
  const [deptLob, setDeptLob] = useState('');
  const [carrierList, setCarrierList] = useState<FranchiseCarrier[]>([]);
  const [showBugForm, setShowBugForm] = useState(false);
  const [bugTitle, setBugTitle] = useState('');
  const [bugCategory, setBugCategory] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugFiles, setBugFiles] = useState<File[]>([]);
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const [bugTicketKey, setBugTicketKey] = useState('');
  const [bugError, setBugError] = useState('');
  const [showItForm, setShowItForm] = useState(false);
  const [itCategory, setItCategory] = useState('');
  const [itSubCategory, setItSubCategory] = useState('');
  const [itDescription, setItDescription] = useState('');
  const [itPolicyNumber, setItPolicyNumber] = useState('');
  const [itUrgent, setItUrgent] = useState(false);
  const [itFiles, setItFiles] = useState<File[]>([]);
  const [itSubmitting, setItSubmitting] = useState(false);
  const [itSubmitted, setItSubmitted] = useState(false);
  const [itTicketKey, setItTicketKey] = useState('');
  const [itError, setItError] = useState('');
  const [showPreChat, setShowPreChat] = useState<'agent' | 'commercial' | 'carrier' | null>(null);
  const [preChatData, setPreChatData] = useState(PRE_CHAT_EMPTY);

  // Session state (transcript, connection status, errors) is owned by the chat
  // provider so it survives this component unmounting — on a route that hides the
  // widget, or while the conversation is popped out.
  const { status, surface, messages, error, isLive } = chat;
  const isLoading = status === 'starting' && surface === 'panel';
  // The in-page panel renders only when this surface owns the conversation, so a
  // popped-out chat is never drawn in both windows at once.
  const isPanelVisible = surface === 'panel'
    && (status === 'open' || status === 'error' || (status === 'ended' && messages.length > 0));

  useEffect(() => {
    if (deptCategory === 'Carrier Relations' && carrierList.length === 0 && franchiseApi.isConfigured()) {
      franchiseApi.getCarriers().then(res => {
        setCarrierList(res.carriers.sort((a, b) => a.name.localeCompare(b.name)));
      }).catch(() => {});
    }
  }, [deptCategory]);

  const handleStartChat = async (chatType: ChatType) => {
    if (!user) {
      chat.setError('Please log in to start a chat');
      return;
    }

    if (!connectChat.isConfigured()) {
      chat.setError('Chat service is not configured');
      return;
    }

    // Refuse to open a second contact while one is still live. Popping out used to
    // re-show the launch buttons, so a second chat could be started and the first
    // left connected in the queue with nobody watching it.
    if (isLive) {
      chat.show();
      return;
    }

    // Ask while still inside the Start Chat click; once the await below yields, the
    // browser no longer treats the request as user-initiated.
    requestNotificationPermission();

    // Show the starting state before the start-chat call is awaited below; opening a
    // Connect contact takes seconds, and a cold API adds to it.
    chat.beginStart();

    try {
      // Only the hotline and the answers to the questions this form actually asked. Who is asking,
      // which branch and agency they belong to, which team answers and which contact flow the chat
      // enters are all resolved by the backend from the verified token — this used to send every one
      // of them, so a caller could route itself anywhere in the instance as anyone (WIPE-1408).
      const session = await connectChat.startChat(chatType, intakeFor(chatType, preChatData));

      await chat.attach({ chatType, details: session });
      // Each chat is about a different call, so the next form starts blank rather than holding this
      // call's insured and policy for the agent to type over. A failed start keeps them for a retry.
      setPreChatData(PRE_CHAT_EMPTY);
    } catch (err: any) {
      console.error('Failed to start chat:', err);
      chat.setError(err.message || 'Failed to start chat. Please try again.');
    }
  };

  // The launch menu sits in the app's top bar; the forms and the chat panel it opens stay here,
  // drawn over the page.
  const triggers = (
    <>
      <button
        className={`chat-trigger${isLive ? ' chat-trigger-live' : ''}`}
        onClick={() => setShowSupportMenu(!showSupportMenu)}
        aria-label="Hotline and support"
        aria-haspopup="menu"
        aria-expanded={showSupportMenu}
        title="Hotline and support"
        data-ph-capture-attribute-action="open-support-menu"
      >
        <HelpIcon fontSize="small" />
      </button>

      {showSupportMenu && (
        <>
          <div className="chat-support-backdrop" onClick={() => setShowSupportMenu(false)} />
          <div className="chat-support-menu" role="menu">
            <button
              role="menuitem"
              className="chat-support-menu-item"
              onClick={() => { setShowSupportMenu(false); if (isLive) chat.show(); else setShowConfirm(true); }}
              disabled={isLoading}
              data-ph-capture-attribute-action="open-agent-hotline"
            >
              {isLive ? 'Return to Chat' : 'Agent Hotline'}
            </button>
            <button
              role="menuitem"
              className="chat-support-menu-item"
              onClick={() => { setShowSupportMenu(false); setShowBugForm(true); setBugSubmitted(false); setBugTitle(''); setBugCategory(''); setBugDescription(''); setBugFiles([]); setBugError(''); }}
            >
              AgencyOS Bug or Feature
            </button>
            <a
              role="menuitem"
              className="chat-support-menu-item"
              href="https://focustech.atlassian.net/servicedesk/customer/portal/7/group/38/create/169"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowSupportMenu(false)}
            >
              All Department Jira Requests
            </a>
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      {actionsContainer && createPortal(triggers, actionsContainer)}

      {showDeptForm && (
        <div className="chat-confirm-backdrop" onClick={() => setShowDeptForm(false)}>
          <div className="chat-confirm" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '380px' }}>
            {deptSubmitted ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p style={{ fontWeight: 600, color: '#0a202e' }}>Request submitted!</p>
                {deptTicketKey && <p style={{ color: '#0a202e', fontSize: '0.9rem', fontWeight: 500 }}>Ticket: {deptTicketKey}</p>}
                {deptError && <p style={{ color: '#f5a623', fontSize: '0.85rem', fontWeight: 500, marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#fff8e1', borderRadius: '4px' }}>{deptError}</p>}
                <p style={{ color: '#666', fontSize: '0.85rem' }}>The team will follow up via email.</p>
                <button className="chat-confirm-no" onClick={() => setShowDeptForm(false)}>Close</button>
              </div>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: '1rem' }}>Get Support from Other Departments</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {deptError && (
                    <div style={{ padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33', fontSize: '0.85rem' }}>
                      {deptError}
                    </div>
                  )}
                  <select
                    value={deptCategory}
                    onChange={(e) => { setDeptCategory(e.target.value); setDeptSubCategory(''); setDeptCarrier(''); setDeptCarrierSearch(''); setDeptLob(''); setDeptError(''); }}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <option value="">Select a department *</option>
                    <option value="Accounting - Commissions">Accounting - Commissions</option>
                    <option value="Accounting - Premium Trust">Accounting - Premium Trust</option>
                    <option value="Applied EPIC Help">Applied EPIC Help</option>
                    <option value="Carrier Relations">Carrier Relations</option>
                    <option value="Compliance">Compliance</option>
                    <option value="Customer Service">Customer Service</option>
                    <option value="Training">Training</option>
                    <option value="Franchise Sales">Franchise Sales</option>
                    <option value="Marketing">Marketing</option>
                    <option value="MIA - Missing Agent Info">MIA - Missing Agent Info</option>
                    <option value="Technology">Technology</option>
                  </select>
                  {deptCategory && (() => {
                    const subMap: Record<string, string[]> = {
                      'Accounting - Commissions': ['Agency Accounting Updates - Change Bank Account/Commission Splits', 'Agency Reports – Agency Statements/Fees, WE+, Scorecards, 1099s', 'AOR Notification', 'Commission Inquiry', 'Other'],
                      'Accounting - Premium Trust': ['Insured Premium Deposit - ACH, Check, Credit Card, Wires', 'Policy Premium Payment Inquiry', 'Refunds - Request/Status/Reissue', 'Request Urgent Payment to Carrier - Past Due/Pending Cancel', 'Other'],
                      'Applied EPIC Help': ['Epic Browser', 'Epic Lockout', 'EPIC Onboarding/Offboarding', 'EPIC Quotes', 'EPIC Report Request', 'General EPIC Configurations/Error', 'Internal AOR Request', 'Other'],
                      'Carrier Relations': ['Carrier Products & State', 'Existing Carrier Appointment', 'New Carrier Appointment', 'Offboard Employee to Existing Franchise', 'Offboarding Franchise', 'Onboard New Employee to Existing Franchise', 'Onboarding New Franchise', 'Password Reset', 'Update Agency Information with Carrier', 'Other'],
                      'Compliance': ['Agency Relocation', 'Change to Agent in Charge (AIC)', 'Contract Management', 'Errors & Omissions / Requesting Certificates of Insurance', 'Licensing', 'Review/Approve Franchise Proposed Sales', 'Third Party Complaints / Litigation', 'Other'],
                      'Customer Service': ['Customer Service Process Training', 'Internal AOR Request', 'Kudos', 'Meeting Request', 'Process Review Request', 'Supervisor Escalation', 'Other'],
                      'Training': ['New Hire Training', 'Sales Training', 'Other'],
                      'Franchise Sales': ['Ancillary Franchise Issue', 'Insurance Franchise Issue', 'Other'],
                      'Marketing': ['AI (Ideas, Suggestions, R2G2)', 'Creative Requests - Physical and Digital', 'Email Signature', 'InsuredMine Campaigns, Lists, and Templates', 'Social Media Requests', 'Unsubscribe', 'We Insure Network updates', 'Website', 'New Email Send Request', 'Stadium Item Request', 'Other'],
                      'MIA - Missing Agent Info': ['Claim an MIA', 'Report an MIA', 'Report MIA from Download'],
                      'Technology': ['1 Password', '8x8 Issue', 'Adobe/PDF', 'AWS/Symbee', 'Carrier Site', 'Computer Setup', 'DocuSign', 'Email/Outlook', 'Hardware', 'InsuredMine', 'Onboarding and Offboarding', 'OneDrive/File Access', 'Password Reset', 'Phishing/Spam', 'Software Issues', 'We Network', 'Other'],
                    };
                    const subs = subMap[deptCategory] || [];
                    if (subs.length === 0) return null;
                    return (
                      <select
                        value={deptSubCategory}
                        onChange={(e) => setDeptSubCategory(e.target.value)}
                        style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                      >
                        <option value="">Select subcategory *</option>
                        {subs.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    );
                  })()}
                  <input
                    type="tel"
                    placeholder="Phone number (optional)"
                    value={deptPhone}
                    onChange={(e) => setDeptPhone(e.target.value)}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="text"
                    placeholder="Policy number (optional)"
                    value={deptPolicyNumber}
                    onChange={(e) => setDeptPolicyNumber(e.target.value)}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="email"
                    placeholder="AIC email address (optional)"
                    value={deptAicEmail}
                    onChange={(e) => setDeptAicEmail(e.target.value)}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  {deptCategory === 'Carrier Relations' && (
                    <>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          placeholder="Search and select carrier..."
                          value={deptCarrierSearch || deptCarrier}
                          onChange={(e) => { setDeptCarrierSearch(e.target.value); setDeptCarrier(''); }}
                          onFocus={() => setDeptCarrierSearch(deptCarrierSearch || '')}
                          style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }}
                        />
                        {deptCarrierSearch !== null && !deptCarrier && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #ddd', borderRadius: '0 0 6px 6px', zIndex: 10 }}>
                            {carrierList
                              .filter(c => c.name.toLowerCase().includes((deptCarrierSearch || '').toLowerCase()))
                              .slice(0, 30)
                              .map(c => (
                                <div
                                  key={c.id}
                                  onClick={() => { setDeptCarrier(c.name); setDeptCarrierSearch(null as any); }}
                                  style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid #f0f0f0' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                                >
                                  {c.name}
                                </div>
                              ))}
                            {carrierList.filter(c => c.name.toLowerCase().includes((deptCarrierSearch || '').toLowerCase())).length === 0 && (
                              <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: '#999' }}>No carriers found</div>
                            )}
                          </div>
                        )}
                      </div>
                      <select
                        value={deptLob}
                        onChange={(e) => setDeptLob(e.target.value)}
                        style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                      >
                        <option value="">Select Line of Business</option>
                        {LOB_KEYWORDS.map(lob => <option key={lob} value={lob}>{lob}</option>)}
                      </select>
                    </>
                  )}
                  <textarea
                    placeholder="How can we help you?"
                    value={deptDescription}
                    onChange={(e) => setDeptDescription(e.target.value)}
                    rows={3}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>Attachments (optional, max 5 files, 10 MB each)</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        const valid = selected.filter(f => f.size <= 10 * 1024 * 1024);
                        setDeptFiles(prev => [...prev, ...valid].slice(0, 5));
                        e.target.value = '';
                      }}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                      style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}
                    />
                    {deptFiles.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0.4rem 0 0' }}>
                        {deptFiles.map((f, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', padding: '0.2rem 0' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                            <button type="button" onClick={() => setDeptFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>X</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="chat-confirm-actions" style={{ marginTop: '1rem' }}>
                  <button
                    className="chat-confirm-yes"
                    disabled={!deptCategory || !deptSubCategory || !deptDescription.trim() || deptSubmitting}
                    onClick={async () => {
                      setDeptSubmitting(true);
                      let attachmentError: string | null = null;
                      try {
                        let attachments: TicketAttachment[] = [];

                        if (deptFiles.length > 0) {
                          try {
                            attachments = await jiraTicketsApi.uploadAttachments(deptFiles);
                          } catch (uploadErr) {
                            const errorMsg = uploadErr instanceof Error ? uploadErr.message : 'Unknown upload error';
                            attachmentError = `Your attachment${deptFiles.length > 1 ? 's' : ''} couldn't be uploaded: ${errorMsg}`;

                            captureError(uploadErr, {
                              context: 'dept_ticket_attachment_upload',
                              userId: user?.email,
                              fileCount: deptFiles.length,
                              files: deptFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
                              category: deptCategory,
                              subcategory: deptSubCategory,
                              timestamp: new Date().toISOString(),
                            });

                            console.error('Department ticket attachment upload failed:', uploadErr);
                          }
                        }

                        const submitter = user?.email || user?.name || 'Unknown';
                        const description = [
                          `h3. Department Support Request`,
                          ``,
                          `||Field||Value||`,
                          `|Submitted by|${submitter}|`,
                          `|Department|${deptCategory}|`,
                          `|Subcategory|${deptSubCategory}|`,
                          ...(deptCategory === 'Carrier Relations' && deptCarrier ? [`|Carrier|${deptCarrier}|`] : []),
                          ...(deptCategory === 'Carrier Relations' && deptLob ? [`|Line of Business|${deptLob}|`] : []),
                          `|Branch|${agency?.branch_code || 'N/A'}|`,
                          `|Agency|${agency?.dba_name || agency?.name || 'N/A'}|`,
                          `|Date|${new Date().toISOString()}|`,
                          ``,
                          `h3. Details`,
                          deptDescription.trim(),
                        ].join('\n');
                        const customFields: Record<string, any> = {
                          customfield_10800: '169',
                          customfield_16164: { value: deptCategory, child: { value: deptSubCategory } },
                          ...(deptPhone ? { customfield_16163: deptPhone } : {}),
                          ...(deptPolicyNumber ? { customfield_16166: deptPolicyNumber } : {}),
                          ...(deptAicEmail ? { customfield_16167: deptAicEmail } : {}),
                        };

                        let ticketData: { ticketKey: string };
                        try {
                          ticketData = await jiraTicketsApi.createTicket({
                            summary: `${deptCategory} - ${deptSubCategory}`,
                            description,
                            customFields,
                            attachments,
                          });
                        } catch (ticketErr) {
                          captureError(ticketErr, {
                            context: 'dept_ticket_creation_failed',
                            userId: user?.email,
                            category: deptCategory,
                            subcategory: deptSubCategory,
                            hasAttachments: attachments.length > 0,
                            timestamp: new Date().toISOString(),
                          });
                          throw ticketErr;
                        }
                        if (ticketData.ticketKey) {
                          setDeptTicketKey(ticketData.ticketKey);
                          if (attachmentError) {
                            setDeptError(attachmentError + ' Your ticket was submitted without the attachment.');
                          }
                        }
                        setDeptSubmitted(true);
                      } catch (err: any) {
                        captureError(err, {
                          context: 'dept_ticket_submission_error',
                          userId: user?.email,
                          category: deptCategory,
                          subcategory: deptSubCategory,
                          timestamp: new Date().toISOString(),
                        });
                        setDeptError(err.message || 'Failed to submit request. Please try again.');
                      } finally {
                        setDeptSubmitting(false);
                      }
                    }}
                  >
                    {deptSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button className="chat-confirm-no" onClick={() => setShowDeptForm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showBugForm && (
        <div className="chat-confirm-backdrop" onClick={() => setShowBugForm(false)}>
          <div className="chat-confirm" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            {bugSubmitted ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p style={{ fontWeight: 600, color: '#0a202e' }}>Request submitted!</p>
                {bugTicketKey && <p style={{ color: '#0a202e', fontSize: '0.9rem', fontWeight: 500 }}>Ticket: {bugTicketKey}</p>}
                {bugError && <p style={{ color: '#f5a623', fontSize: '0.85rem', fontWeight: 500, marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#fff8e1', borderRadius: '4px' }}>{bugError}</p>}
                <p style={{ color: '#666', fontSize: '0.85rem' }}>Our product team will review your request and follow up via email.</p>
                <button className="chat-confirm-no" onClick={() => setShowBugForm(false)}>Close</button>
              </div>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: '1rem' }}>AgencyOS Bug or Feature Request</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {bugError && (
                    <div style={{ padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33', fontSize: '0.85rem' }}>
                      {bugError}
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Title *"
                    value={bugTitle}
                    onChange={(e) => { setBugTitle(e.target.value); setBugError(''); }}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <select
                    value={bugCategory}
                    onChange={(e) => { setBugCategory(e.target.value); setBugError(''); }}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <option value="">Select a category *</option>
                    <option value="Bug">Report a Bug</option>
                    <option value="New Feature">New Feature</option>
                    <option value="Improvement">Improvement to Existing Feature</option>
                    <option value="Integration">Integration / Connectivity</option>
                    <option value="UI/UX">UI/UX Enhancement</option>
                    <option value="Other">Other</option>
                  </select>
                  <textarea
                    placeholder="Describe the issue or feature request..."
                    value={bugDescription}
                    onChange={(e) => setBugDescription(e.target.value)}
                    rows={4}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>Attachments (optional, max 5 files, 10 MB each)</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        const valid = selected.filter(f => f.size <= 10 * 1024 * 1024);
                        setBugFiles(prev => [...prev, ...valid].slice(0, 5));
                        e.target.value = '';
                      }}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                      style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}
                    />
                    {bugFiles.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0.4rem 0 0' }}>
                        {bugFiles.map((f, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', padding: '0.2rem 0' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                            <button type="button" onClick={() => setBugFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>X</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="chat-confirm-actions" style={{ marginTop: '1rem' }}>
                  <button
                    className="chat-confirm-yes"
                    disabled={!bugTitle.trim() || !bugCategory || bugSubmitting}
                    onClick={async () => {
                      setBugSubmitting(true);
                      let attachmentError: string | null = null;
                      try {
                        let attachments: TicketAttachment[] = [];

                        if (bugFiles.length > 0) {
                          try {
                            attachments = await jiraTicketsApi.uploadAttachments(bugFiles);
                          } catch (uploadErr) {
                            const errorMsg = uploadErr instanceof Error ? uploadErr.message : 'Unknown upload error';
                            attachmentError = `Your attachment${bugFiles.length > 1 ? 's' : ''} couldn't be uploaded: ${errorMsg}`;

                            captureError(uploadErr, {
                              context: 'bug_report_attachment_upload',
                              userId: user?.email,
                              fileCount: bugFiles.length,
                              files: bugFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
                              category: bugCategory,
                              timestamp: new Date().toISOString(),
                            });

                            console.error('Bug report attachment upload failed:', uploadErr);
                          }
                        }

                        const submitter = user?.email || user?.name || 'Unknown';
                        const description = [
                          `h3. Feature Request`,
                          ``,
                          `||Field||Value||`,
                          `|Submitted by|${submitter}|`,
                          `|Branch|${agency?.branch_code || 'N/A'}|`,
                          `|Agency|${agency?.dba_name || agency?.name || 'N/A'}|`,
                          `|Date|${new Date().toISOString()}|`,
                          `|Category|${bugCategory}|`,
                          ``,
                          `h3. Description`,
                          bugDescription.trim() || '_No description provided_',
                        ].join('\n');
                        let ticketData: { ticketKey: string };
                        try {
                          // Files into the WIPE product backlog rather than the franchise
                          // service desk (WIPE-1515). Assignee, labels and the Jira field the
                          // category maps to all belong to that destination, so they are the
                          // backend's to set — none of them is a WEFR value any more.
                          ticketData = await jiraTicketsApi.createTicket({
                            summary: `AgencyOS - ${bugCategory} - ${bugTitle.trim()}`,
                            description,
                            destination: 'agencyos-product',
                            category: bugCategory,
                            attachments,
                          });
                        } catch (ticketErr) {
                          captureError(ticketErr, {
                            context: 'bug_report_creation_failed',
                            userId: user?.email,
                            category: bugCategory,
                            hasAttachments: attachments.length > 0,
                            timestamp: new Date().toISOString(),
                          });
                          throw ticketErr;
                        }
                        if (ticketData.ticketKey) {
                          setBugTicketKey(ticketData.ticketKey);
                          if (attachmentError) {
                            setBugError(attachmentError + ' Your ticket was submitted without the attachment.');
                          }
                        }
                        setBugSubmitted(true);
                      } catch (err: any) {
                        captureError(err, {
                          context: 'bug_report_submission_error',
                          userId: user?.email,
                          category: bugCategory,
                          timestamp: new Date().toISOString(),
                        });
                        setBugError(err.message || 'Failed to submit bug report. Please try again.');
                      } finally {
                        setBugSubmitting(false);
                      }
                    }}
                  >
                    {bugSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button className="chat-confirm-no" onClick={() => setShowBugForm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showItForm && (
        <div className="chat-confirm-backdrop" onClick={() => setShowItForm(false)}>
          <div className="chat-confirm" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            {itSubmitted ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p style={{ fontWeight: 600, color: '#0a202e' }}>IT Support request submitted!</p>
                {itTicketKey && <p style={{ color: '#0a202e', fontSize: '0.9rem', fontWeight: 500 }}>Ticket: {itTicketKey}</p>}
                {itError && <p style={{ color: '#f5a623', fontSize: '0.85rem', fontWeight: 500, marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#fff8e1', borderRadius: '4px' }}>{itError}</p>}
                <p style={{ color: '#666', fontSize: '0.85rem' }}>The IT team will follow up via email.</p>
                <button className="chat-confirm-no" onClick={() => setShowItForm(false)}>Close</button>
              </div>
            ) : (
              <>
                <p style={{ fontWeight: 600, marginBottom: '1rem' }}>IT Support Request</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {itError && (
                    <div style={{ padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33', fontSize: '0.85rem' }}>
                      {itError}
                    </div>
                  )}
                  <select
                    value={itCategory}
                    onChange={(e) => { setItCategory(e.target.value); setItSubCategory(''); setItError(''); }}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  >
                    <option value="">Select a category *</option>
                    <option value="1 Password">1 Password</option>
                    <option value="8x8 Issue">8x8 Issue</option>
                    <option value="Adobe/PDF">Adobe/PDF</option>
                    <option value="AWS/Symbee">AWS/Symbee</option>
                    <option value="Carrier Site">Carrier Site</option>
                    <option value="Computer Setup">Computer Setup</option>
                    <option value="DocuSign">DocuSign</option>
                    <option value="Email/Outlook">Email/Outlook</option>
                    <option value="Hardware">Hardware</option>
                    <option value="InsuredMine">InsuredMine</option>
                    <option value="Onboarding and Offboarding">Onboarding and Offboarding</option>
                    <option value="OneDrive/File Access">OneDrive/File Access</option>
                    <option value="Password Reset">Password Reset</option>
                    <option value="Phishing/Spam">Phishing/Spam</option>
                    <option value="Software Issues">Software Issues</option>
                    <option value="We Network">We Network</option>
                    <option value="Other">Other</option>
                  </select>
                  {itCategory && (() => {
                    const itSubMap: Record<string, string[]> = {
                      '1 Password': ['Access Issue', 'New Vault Request', 'Shared Vault', 'Other'],
                      '8x8 Issue': ['Call Quality', 'Voicemail', 'Configuration', 'Login Issue', 'Other'],
                      'Adobe/PDF': ['Installation', 'License Issue', 'PDF Not Opening', 'Other'],
                      'AWS/Symbee': ['Login Issue', 'Configuration', 'Performance', 'Other'],
                      'Carrier Site': ['Login Issue', 'Site Not Loading', 'Access Request', 'Other'],
                      'Computer Setup': ['New Computer', 'Reinstall/Reimage', 'Dual Monitor', 'Docking Station', 'Other'],
                      'DocuSign': ['Login Issue', 'Template Help', 'Envelope Issue', 'Other'],
                      'Email/Outlook': ['Cannot Send/Receive', 'Calendar Issue', 'Signature', 'Shared Mailbox', 'Other'],
                      'Hardware': ['Monitor', 'Keyboard/Mouse', 'Headset', 'Printer', 'Other'],
                      'InsuredMine': ['Login Issue', 'Campaign Issue', 'Integration', 'Other'],
                      'Onboarding and Offboarding': ['New Hire Setup', 'Termination', 'Transfer', 'Other'],
                      'OneDrive/File Access': ['Sync Issue', 'Permission Request', 'File Recovery', 'Other'],
                      'Password Reset': ['Microsoft/O365', 'Carrier Site', 'Other Application', 'Other'],
                      'Phishing/Spam': ['Report Suspicious Email', 'Compromised Account', 'Other'],
                      'Software Issues': ['Installation', 'Crash/Freeze', 'Update Needed', 'License', 'Other'],
                      'We Network': ['Login Issue', 'Page Not Loading', 'Feature Help', 'Other'],
                      'Other': ['Other'],
                    };
                    const subs = itSubMap[itCategory] || [];
                    if (subs.length <= 1 && subs[0] === 'Other') return null;
                    return (
                      <select
                        value={itSubCategory}
                        onChange={(e) => setItSubCategory(e.target.value)}
                        style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                      >
                        <option value="">Select subcategory *</option>
                        {subs.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    );
                  })()}
                  <textarea
                    placeholder="Request details *"
                    value={itDescription}
                    onChange={(e) => setItDescription(e.target.value)}
                    rows={4}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                  <input
                    type="text"
                    placeholder="Policy number (optional)"
                    value={itPolicyNumber}
                    onChange={(e) => setItPolicyNumber(e.target.value)}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={itUrgent}
                      onChange={(e) => setItUrgent(e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontWeight: 500 }}>Is this request urgent?</span>
                  </label>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>Attachments (optional, max 5 files, 10 MB each)</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        const valid = selected.filter(f => f.size <= 10 * 1024 * 1024);
                        setItFiles(prev => [...prev, ...valid].slice(0, 5));
                        e.target.value = '';
                      }}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                      style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}
                    />
                    {itFiles.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0.4rem 0 0' }}>
                        {itFiles.map((f, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', padding: '0.2rem 0' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                            <button type="button" onClick={() => setItFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>X</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="chat-confirm-actions" style={{ marginTop: '1rem' }}>
                  <button
                    className="chat-confirm-yes"
                    disabled={!itCategory || !itSubCategory || !itDescription.trim() || itSubmitting}
                    onClick={async () => {
                      setItSubmitting(true);
                      let attachmentError: string | null = null;
                      try {
                        let attachments: TicketAttachment[] = [];

                        if (itFiles.length > 0) {
                          try {
                            attachments = await jiraTicketsApi.uploadAttachments(itFiles);
                          } catch (uploadErr) {
                            const errorMsg = uploadErr instanceof Error ? uploadErr.message : 'Unknown upload error';
                            attachmentError = `Your attachment${itFiles.length > 1 ? 's' : ''} couldn't be uploaded: ${errorMsg}`;

                            captureError(uploadErr, {
                              context: 'it_ticket_attachment_upload',
                              userId: user?.email,
                              fileCount: itFiles.length,
                              files: itFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
                              category: itCategory,
                              subcategory: itSubCategory,
                              timestamp: new Date().toISOString(),
                            });

                            console.error('IT ticket attachment upload failed:', uploadErr);
                          }
                        }

                        const submitter = user?.email || user?.name || 'Unknown';
                        const description = [
                          `h3. IT Support Request`,
                          ``,
                          `||Field||Value||`,
                          `|Submitted by|${submitter}|`,
                          `|Category|Technology - ${itCategory}|`,
                          `|Subcategory|${itSubCategory}|`,
                          `|Branch|${agency?.branch_code || 'N/A'}|`,
                          `|Agency|${agency?.dba_name || agency?.name || 'N/A'}|`,
                          `|Date|${new Date().toISOString()}|`,
                          itPolicyNumber ? `|Policy Number|${itPolicyNumber}|` : '',
                          `|Urgent|${itUrgent ? 'Yes' : 'No'}|`,
                          ``,
                          `h3. Details`,
                          itDescription.trim(),
                        ].filter(Boolean).join('\n');

                        const itCustomFields: Record<string, any> = {
                          customfield_10800: '169',
                          customfield_16164: { value: 'Technology', child: { value: itCategory } },
                          ...(itPolicyNumber ? { customfield_16166: itPolicyNumber } : {}),
                        };

                        let ticketData: { ticketKey: string };
                        try {
                          ticketData = await jiraTicketsApi.createTicket({
                            summary: `IT Support - ${itCategory} - ${itSubCategory}${itUrgent ? ' [URGENT]' : ''}`,
                            description,
                            customFields: itCustomFields,
                            attachments,
                          });
                        } catch (ticketErr) {
                          captureError(ticketErr, {
                            context: 'it_ticket_creation_failed',
                            userId: user?.email,
                            category: itCategory,
                            subcategory: itSubCategory,
                            hasAttachments: attachments.length > 0,
                            timestamp: new Date().toISOString(),
                          });
                          throw ticketErr;
                        }
                        if (ticketData.ticketKey) {
                          setItTicketKey(ticketData.ticketKey);
                          if (attachmentError) {
                            setItError(attachmentError + ' Your ticket was submitted without the attachment.');
                          }
                        }
                        setItSubmitted(true);
                      } catch (err: any) {
                        captureError(err, {
                          context: 'it_ticket_submission_error',
                          userId: user?.email,
                          category: itCategory,
                          subcategory: itSubCategory,
                          timestamp: new Date().toISOString(),
                        });
                        setItError(err.message || 'Failed to submit IT support request. Please try again.');
                      } finally {
                        setItSubmitting(false);
                      }
                    }}
                  >
                    {itSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button className="chat-confirm-no" onClick={() => setShowItForm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Chat type selector */}
      {showConfirm && (
        <div className="chat-confirm-backdrop" onClick={() => setShowConfirm(false)}>
          <div className="chat-confirm" onClick={(e) => e.stopPropagation()}>
            <p>Choose a hotline to connect with:</p>
            <div className="chat-confirm-actions">
              <button
                className="chat-confirm-yes"
                onClick={() => { setShowConfirm(false); setShowPreChat('agent'); }}
              >
                Agent Hotline
              </button>
              <button
                className="chat-confirm-yes"
                onClick={() => { setShowConfirm(false); setShowPreChat('commercial'); }}
              >
                Commercial Hotline
              </button>
              <button
                className="chat-confirm-yes"
                onClick={() => { setShowConfirm(false); setShowPreChat('carrier'); }}
              >
                Carrier Relations
              </button>
              <button
                className="chat-confirm-no"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-chat intake form */}
      {showPreChat && (
        <div className="chat-confirm-backdrop" onClick={() => setShowPreChat(null)}>
          <div className="chat-confirm" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '380px' }}>
            <p style={{ fontWeight: 600, marginBottom: '1rem' }}>
              {showPreChat === 'agent' && 'Agent Hotline — Tell us about your call'}
              {showPreChat === 'commercial' && 'Commercial Hotline — Tell us about your call'}
              {showPreChat === 'carrier' && 'Carrier Relations — Tell us about your inquiry'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {(showPreChat === 'agent' || showPreChat === 'commercial') && (
                <>
                  <input
                    type="text"
                    placeholder="Insured's / Business Name"
                    value={preChatData.insuredName}
                    onChange={(e) => setPreChatData(prev => ({ ...prev, insuredName: e.target.value }))}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#666', marginBottom: '0.35rem' }}>Select a LOB (Policy Type)</label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {['HO3', 'DP3', 'GLIA'].map(lob => (
                        <button
                          key={lob}
                          type="button"
                          onClick={() => setPreChatData(prev => ({ ...prev, lob: prev.lob === lob ? '' : lob }))}
                          style={{ padding: '0.4rem 0.8rem', borderRadius: '16px', border: 'none', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', background: preChatData.lob === lob ? '#0a202e' : '#e0e0e0', color: preChatData.lob === lob ? '#20cbd4' : '#333' }}
                        >
                          {lob}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Lookup code or policy"
                    value={preChatData.lookupCode}
                    onChange={(e) => setPreChatData(prev => ({ ...prev, lookupCode: e.target.value }))}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                </>
              )}
              {showPreChat === 'carrier' && (
                <>
                  <input
                    type="tel"
                    placeholder="Best contact number (optional)"
                    value={preChatData.contactNumber}
                    onChange={(e) => setPreChatData(prev => ({ ...prev, contactNumber: e.target.value }))}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="text"
                    placeholder="Carrier name (optional)"
                    value={preChatData.carrierName}
                    onChange={(e) => setPreChatData(prev => ({ ...prev, carrierName: e.target.value }))}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="text"
                    placeholder="Carrier username assigned (optional)"
                    value={preChatData.carrierUsername}
                    onChange={(e) => setPreChatData(prev => ({ ...prev, carrierUsername: e.target.value }))}
                    style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                </>
              )}
              <textarea
                placeholder="How can we help you?"
                value={preChatData.helpDescription}
                onChange={(e) => setPreChatData(prev => ({ ...prev, helpDescription: e.target.value }))}
                rows={3}
                style={{ padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
            <div className="chat-confirm-actions" style={{ marginTop: '1rem' }}>
              <button
                className="chat-confirm-yes"
                onClick={() => {
                  const type = showPreChat;
                  setShowPreChat(null);
                  handleStartChat(type!);
                }}
                disabled={!preChatData.helpDescription.trim()}
              >
                Start Chat
              </button>
              <button
                className="chat-confirm-no"
                onClick={() => { setShowPreChat(null); setPreChatData(PRE_CHAT_EMPTY); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat panel */}
      {isPanelVisible && (
        <div className="chat-widget">
          {error ? (
            <div className="chat-error">
              <p>{error}</p>
              <button onClick={() => chat.reset()}>Close</button>
            </div>
          ) : (
            <div className="chat-container">
              <div className="chat-header">
                <h3>{chat.title}</h3>
                <div className="chat-header-actions">
                  <button
                    className="close-btn chat-header-btn"
                    title="Pop out"
                    aria-label="Pop out"
                    onClick={() => chat.popOut()}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </button>
                  <button
                    className="close-btn chat-header-btn chat-header-btn-end"
                    title="End chat"
                    onClick={() => chat.end()}
                    disabled={status === 'ended'}
                  >
                    End
                  </button>
                  {/* ✕ minimizes a live chat rather than ending it; the launch
                      button turns into "Return to Chat". End is the only control
                      that disconnects. */}
                  <button
                    className="close-btn"
                    title="Minimize chat"
                    aria-label="Minimize chat"
                    onClick={() => chat.hide()}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              </div>
              <ChatTranscript messages={messages} />
              <ChatComposer disabled={status === 'ended'} onSend={chat.send} />
              <button
                className="chat-download-btn"
                onClick={() => downloadTranscript(messages, chat.title)}
                disabled={messages.length === 0}
              >
                <FileDownloadIcon fontSize="small" />
                Download Transcript
              </button>
            </div>
          )}
        </div>
      )}
      {isLoading && (
        <div className="chat-widget">
          <div className="chat-loading">Starting chat...</div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
